import dayjs from 'dayjs';
import { Hit } from 'react-instantsearch-core';
import { SearchObject } from './Models';
import { connectHighlight } from 'react-instantsearch-dom';
import Highlight from './Highlight';
import Tag from './Tag';
import { P2GBox, P2GDate, P2GDivider, P2GGrid, P2GText } from '@parcel2go/core-ui';
import { onlyUnique } from '~/lib/utils/array';
import { AppCard } from '~/components/card/app-card';
import { sanitiseSourceForHit } from '~/apps/smartsend/components/search/ToSmartSendItem';
import { GetDefaultSearchHitTags } from './GetDefaultSearchHitTags';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';

export interface IHitProps {
  hit: Hit<SearchObject>;
}
const CustomHighlight = connectHighlight(Highlight);

const HitLayout: React.FC<React.PropsWithChildren<IHitProps>> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const tags = GetDefaultSearchHitTags(props.hit);
  return (
    <AppCard>
      <P2GBox p={2}>
        <P2GGrid container direction="column" spacing={1} data-sentry-mask>
          <P2GGrid item>
            <P2GGrid container alignItems="center" alignContent="center" spacing={1}>
              <P2GGrid item flexGrow={1}>
                <P2GText variant="subtitle2">
                  {props.hit.prefix}
                  <CustomHighlight attribute="orderLineId" hit={props.hit} />
                  <P2GText variant="caption">
                    <P2GDate ml={1} variant="caption" date={dayjs.unix(props.hit.paidDate).toDate()} /> /{' '}
                    {sanitiseSourceForHit(props.hit)}
                  </P2GText>
                </P2GText>
              </P2GGrid>
              <P2GGrid item>
                <P2GText>
                  <strong>{t('ORDER_SEARCH_TRACKING_STATUS_TITLE', { defaultValue: 'Tracking Status:' })}</strong>
                </P2GText>
              </P2GGrid>
              <P2GGrid item>
                <Tag
                  label={props.hit.trackingStatus}
                  color={props.hit.trackingStatus === 'Delivered' ? 'success' : 'info'}
                />
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
          <P2GGrid item>
            <P2GText component="div">
              <CustomHighlight attribute="deliveryAddress.name" hit={props.hit} />
              &nbsp;
              <CustomHighlight attribute="deliveryAddress.property" hit={props.hit} />
              &nbsp;
              <CustomHighlight attribute="deliveryAddress.street" hit={props.hit} />
              &nbsp;
              <CustomHighlight attribute="deliveryAddress.town" hit={props.hit} />
              &nbsp;
              <CustomHighlight attribute="deliveryAddress.postcode" hit={props.hit} />
              &nbsp;
              <CustomHighlight attribute="deliveryAddress.iso3Code" hit={props.hit} />
            </P2GText>
            <P2GText component="div" mt={1}>
              <div>
                <CustomHighlight attribute="deliveryAddress.email" hit={props.hit} />
              </div>
              <div>
                <CustomHighlight attribute="deliveryAddress.telephone" hit={props.hit} />
              </div>
            </P2GText>
          </P2GGrid>
          {props.hit.smartSend.count > 0 ? (
            <P2GGrid item>
              <P2GGrid container spacing={1} direction="column">
                {props.hit.smartSend.items.map((p, i) => (
                  <P2GGrid item key={p.id}>
                    <P2GGrid container spacing={1} alignContent="center" alignItems="center">
                      <P2GGrid item>
                        <P2GText>
                          <CustomHighlight attribute={`smartSend.items[${i}].description`} hit={props.hit} />
                        </P2GText>
                      </P2GGrid>
                      <P2GGrid item>
                        <P2GText>
                          <CustomHighlight attribute={`smartSend.items[${i}].reference`} hit={props.hit} />
                        </P2GText>
                      </P2GGrid>
                      <P2GGrid item>
                        <P2GText>
                          <CustomHighlight attribute={`smartSend.items[${i}].buyerId`} hit={props.hit} />
                        </P2GText>
                      </P2GGrid>
                      <P2GGrid item>
                        <P2GText>
                          <CustomHighlight attribute={`smartSend.items[${i}].sku`} hit={props.hit} />
                        </P2GText>
                      </P2GGrid>
                    </P2GGrid>
                  </P2GGrid>
                ))}
              </P2GGrid>
            </P2GGrid>
          ) : (
            <P2GGrid item>
              <P2GGrid container spacing={1}>
                {props.hit.parcels.map((p, i) => (
                  <P2GGrid item key={`${props.hit.orderLineId}_${p.id}`}>
                    <P2GGrid container spacing={1} alignItems="center">
                      <P2GGrid item>
                        <P2GText>
                          <CustomHighlight attribute={`parcels[${i}].contents`} hit={props.hit} />
                        </P2GText>
                      </P2GGrid>
                      {p.parcelNumbers.filter(onlyUnique).map((n, ii) => (
                        <P2GGrid item key={`${props.hit.orderLineId}_${p.id}_${n}`}>
                          <P2GText>
                            <CustomHighlight attribute={`parcels[${i}].parcelNumbers[${ii}]`} hit={props.hit} />
                          </P2GText>
                        </P2GGrid>
                      ))}
                    </P2GGrid>
                  </P2GGrid>
                ))}
              </P2GGrid>
            </P2GGrid>
          )}
          <P2GGrid item>
            <P2GDivider />
          </P2GGrid>
          <P2GGrid item>
            <P2GGrid container alignContent="center" alignItems="center" spacing={1}>
              {tags.map((l) => (
                <P2GGrid item flexShrink={1} key={l}>
                  <Tag label={l} />
                </P2GGrid>
              ))}
            </P2GGrid>
          </P2GGrid>
          {props.children && (
            <>
              <P2GGrid item>
                <P2GDivider />
              </P2GGrid>
              <P2GGrid item>{props.children}</P2GGrid>
            </>
          )}
        </P2GGrid>
      </P2GBox>
    </AppCard>
  );
};

export default HitLayout;
